import { ICoreContext } from '@msdyn365-commerce/core-internal';
import { VariantDetails } from '../../../integrations/relewise/types/mapped';
import { getCartState } from '@msdyn365-commerce/global-state';
import { useState } from 'react';
import { ProductType } from '@msdyn365-commerce/retail-proxy';
import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import { NEW_BUYBOX_MODULE_NAME } from '../cus-new-buybox.const';

interface UseBuyboxAddToCartArgs {
    context: ICoreContext<{
        [x: string]: any;
    }>;
    selectedVariant: VariantDetails | undefined;
    productItemId: string | undefined;
    productRecordId: number | undefined;
    unitOfMeasure: string | undefined;
    telemetry: ITelemetry;
}

export const useBuyboxAddToCart = ({
    context,
    selectedVariant,
    productItemId,
    productRecordId,
    unitOfMeasure,
    telemetry
}: UseBuyboxAddToCartArgs) => {
    const [addToCartLoading, setAddToCartLoading] = useState(false);
    const [hasError, setHasError] = useState<boolean>(false);

    const handleAddToCartClick = async () => {
        const { variantRecordId, basePrice, finalPrice } = selectedVariant ?? {};
        const catalogId = context.request.apiSettings.catalogId;

        try {
            if (!variantRecordId) {
                throw new Error('Missing variantRecordId');
            }
            if (!basePrice || !finalPrice) {
                throw new Error('Missing variant price data');
            }
            if (!unitOfMeasure) {
                throw new Error('Missing unitOfMeasure');
            }
            if (catalogId === undefined) {
                throw new Error('Missing catalogId');
            }

            setAddToCartLoading(true);
            setHasError(false);

            const cartState = await getCartState(context.actionContext);
            const addToCartResult = await cartState.addProductToCart({
                product: {
                    RecordId: variantRecordId,
                    ItemId: productItemId,
                    MasterProductId: productRecordId,
                    Name: selectedVariant?.displayName,
                    BasePrice: basePrice,
                    AdjustedPrice: finalPrice,
                    Price: basePrice,
                    ProductTypeValue: ProductType.Variant,
                    DefaultUnitOfMeasure: unitOfMeasure
                },
                count: 1,
                catalogId,
                enableStockCheck: context.app.config.enableStockCheck
            });
            if (addToCartResult.status === 'FAILED') {
                throw new Error(addToCartResult.errorDetails?.LocalizedMessage || 'Add to cart request failed');
            }
        } catch (error) {
            setHasError(true);
            const message = `[${NEW_BUYBOX_MODULE_NAME}] Error adding to cart: ${error instanceof Error ? error.message : String(error)}`;
            console.error(message);
            telemetry.error(message);
        }

        setAddToCartLoading(false);
    };

    return { handleAddToCartClick, addToCartLoading, hasError };
};
